import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LuxPlusOfferVoucherCallout from 'luxPlus/components/LuxPlusOfferVoucherCallout'
import type { OfferPagePriceMode } from 'contexts/OfferPage/offerPageStateReducer'
import AccommodationPackagePriceSkeleton from 'components/AccommodationKit/AccommodationPackageSelection/AccommodationPackagePriceSkeleton'

const Savings = styled(PriceRowValueDiscount)`
  border-radius: ${rem(2)};
`

const Price = styled(VerticalSpacer)`
  align-content: start;
`

interface Props {
  offer: App.Offer
  packageView: App.OfferPackageOptionView
  checkIn?: string
  quoteScope?: 'from' | 'selected'
  selectedQuantity?: number
  disclosures?: ReactNode
  fetching?: boolean
  showPointsPrice: boolean
  pointsPrice: ReactNode
  displayPrice: number
  displayMemberPrice?: number
  displayValue?: number
  showDiscount: boolean
  discountPercent?: number
  priceMode: OfferPagePriceMode
}

/** Flash-only Option D framing around the existing offer-page quote and loyalty calculations. */
function FlashPackageSelectionPrice({ offer, packageView, checkIn, quoteScope, selectedQuantity, disclosures, fetching, showPointsPrice, pointsPrice, displayPrice, displayMemberPrice, displayValue, showDiscount, discountPercent, priceMode }: Props) {
  const intl = useIntl()
  const caption = checkIn && quoteScope !== 'from' ? intl.formatMessage({
    id: '2QiWJOWP73WPkcJapjWu',
    defaultMessage: '{count, plural, one {# night} other {# nights}}',
    description: 'Duration in nights for flash hotel hotel-only pricing with dates',
  }, { count: packageView.package.duration }) : intl.formatMessage({
    id: 'EBcBnkXr_D1XZCg2Vb8d',
    defaultMessage: '{count, plural, one {# night} other {# nights}} from',
    description: 'Duration in nights from for flash hotel hotel-only pricing without dates',
  }, { count: packageView.package.duration })

  if (fetching) return <AccommodationPackagePriceSkeleton />

  return <Price gap={4} data-testid="flash-option-d-price">
    {showDiscount && !!discountPercent && <Savings discountPercentage={discountPercent} type="estimate" />}
    <LuxPlusOfferVoucherCallout offer={offer} mode="offer" align="start" />
    <PriceRowPriceCaption>{caption}</PriceRowPriceCaption>
    {!!selectedQuantity && <PriceRowPriceCaption>{intl.formatMessage({
      id: 'Ufi4OWGrvSfpyws11fhx',
      defaultMessage: '{count, plural, one {# selected room} other {# selected rooms}}',
      description: 'Explains that the package card price totals only the guest rooms assigned to this selected package',
    }, { count: selectedQuantity })}
    </PriceRowPriceCaption>}
    {pointsPrice}
    {!showPointsPrice && <LuxPlusPriceStack
      size="M"
      price={displayPrice}
      memberPrice={displayMemberPrice}
      saleUnit={priceMode === 'perPerson' ? 'person' : 'total'}
      testid="offer-package-price"
      offerConfig={offer.luxPlus}
    />}
    {displayValue !== undefined && <PriceRowValueDiscountWithCaption originalValue={displayValue} size="M" />}
    <PriceRowTaxesAndFees />
    {disclosures}
  </Price>
}

export default FlashPackageSelectionPrice
